/**
 * Instruction Choice – Gedruckte Anleitung als Bundle-Item
 * =========================================================
 * Fügt die gedruckte Anleitung als zusätzliches items[]-Element
 * ins Bundle-Formular ein, mit derselben _BundleID wie die anderen
 * Bundle-Items → wird im Warenkorb gruppiert und gemeinsam entfernt.
 *
 * Reagiert auf Bundle-Rebuilds (Farb-/Größenwechsel) via MutationObserver
 * und synchronisiert die Instruction-Inputs automatisch.
 */

(function () {
  'use strict';

  const MARKER_ATTR = 'data-instruction-input';

  class InstructionChoice {
    constructor(container) {
      this.container = container;
      this.variantId = container.dataset.printedVariantId;
      this.digitalVariantId = container.dataset.digitalVariantId || '';
      this.bundleTitle = container.dataset.bundleTitle;
      this.formId = container.dataset.formId;
      this.isAvailable = container.dataset.printedAvailable === 'true';
      this.isFree = container.dataset.isFree === 'true';

      // Formular finden
      this.form = document.getElementById(this.formId);
      if (!this.form) {
        this.form = container.closest('product-form')?.querySelector('form[data-type="add-to-cart-form"]');
      }
      if (!this.form) {
        this.form = document.querySelector('form[data-type="add-to-cart-form"]');
      }
      if (!this.form) {
        console.warn('[InstructionChoice] Kein Formular gefunden');
        return;
      }

      // Radio-Buttons
      this.radios = container.querySelectorAll('input[name="instruction_type"]');
      this.options = container.querySelectorAll('.instruction-option');

      // Events: Radio-Change
      this.radios.forEach(function (radio) {
        radio.addEventListener('change', this._onRadioChange.bind(this));
      }.bind(this));

      // MutationObserver: Bundle-Inputs werden bei Farb-/Größenwechsel neu erstellt
      this._syncDebounceTimer = null;
      this._observer = new MutationObserver(this._onFormMutation.bind(this));
      this._observer.observe(this.form, { childList: true, subtree: true });

      // Initial sync + ext-link visibility
      this._toggleExtLink();
      this.sync();
    }

    /**
     * Radio-Change Handler — aktualisiert Styling und synct Inputs
     */
    _onRadioChange() {
      // Styling aktualisieren
      this.options.forEach(function (opt) {
        var radio = opt.querySelector('input[type="radio"]');
        if (radio && radio.checked) {
          opt.classList.add('instruction-option--selected');
        } else {
          opt.classList.remove('instruction-option--selected');
        }
      });

      // Ext-Link-Hinweis nur bei "Ohne gedruckte Anleitung" zeigen
      this._toggleExtLink();

      this.sync();

      // Preis neu berechnen (Strickset-Seite rechnet die gewählte Anleitung mit ein)
      var handler = document.querySelector('strickset-handler');
      if (handler && typeof handler.calcPrice === 'function') handler.calcPrice();
    }

    /**
     * Zeigt/versteckt den externen Anleitungs-Hinweis je nach Auswahl
     */
    _toggleExtLink() {
      var extBox = this.container.querySelector('.instruction-notice-compact--inline');
      if (!extBox) return;
      extBox.style.display = this.getSelected() === 'digital' ? '' : 'none';
    }

    /**
     * MutationObserver Callback — debounced sync nach Bundle-Rebuild
     */
    _onFormMutation(mutations) {
      // Nur reagieren wenn items[]-Inputs hinzugefügt/entfernt wurden
      // (nicht auf unsere eigenen Instruction-Inputs)
      var hasRelevantChange = false;
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        var nodes = [];
        for (var j = 0; j < mutation.addedNodes.length; j++) nodes.push(mutation.addedNodes[j]);
        for (var k = 0; k < mutation.removedNodes.length; k++) nodes.push(mutation.removedNodes[k]);
        for (var n = 0; n < nodes.length; n++) {
          var node = nodes[n];
          if (node.nodeType === 1 && node.hasAttribute && !node.hasAttribute(MARKER_ATTR)) {
            var name = node.getAttribute && node.getAttribute('name');
            if (name && name.indexOf('items[') === 0) {
              hasRelevantChange = true;
              break;
            }
          }
        }
        if (hasRelevantChange) break;
      }

      if (!hasRelevantChange) return;

      // Debounce: Bundle-Rebuild erzeugt viele DOM-Änderungen auf einmal
      clearTimeout(this._syncDebounceTimer);
      this._syncDebounceTimer = setTimeout(this.sync.bind(this), 150);
    }

    /**
     * Gibt den aktuell gewählten Wert zurück ('digital' oder 'printed')
     */
    getSelected() {
      for (var i = 0; i < this.radios.length; i++) {
        if (this.radios[i].checked) return this.radios[i].value;
      }
      return 'digital';
    }

    /**
     * Findet den höchsten verwendeten items[]-Index im Formular
     */
    _getMaxItemIndex() {
      var inputs = this.form.querySelectorAll('input[name^="items["]');
      var max = -1;
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].hasAttribute(MARKER_ATTR)) continue;
        var match = inputs[i].name.match(/^items\[(\d+)\]/);
        if (match) {
          var idx = parseInt(match[1], 10);
          if (idx > max) max = idx;
        }
      }
      return max;
    }

    /**
     * Liest die _BundleID der anderen Bundle-Items
     */
    _getBundleId() {
      var input = this.form.querySelector('input[name$="[properties][_BundleID]"]:not([' + MARKER_ATTR + '])');
      return input ? input.value : String(Date.now());
    }

    /**
     * Hilfsfunktion: Hidden Input erstellen und ins Formular einfügen
     */
    _appendInput(name, value) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.setAttribute(MARKER_ATTR, '');
      this.form.appendChild(input);
    }

    /**
     * Hauptlogik: Instruction-Inputs einfügen oder entfernen
     */
    sync() {
      // Alte Instruction-Inputs entfernen
      var oldInputs = this.form.querySelectorAll('[' + MARKER_ATTR + ']');
      for (var i = 0; i < oldInputs.length; i++) {
        oldInputs[i].remove();
      }

      var selected = this.getSelected();

      // Prüfen ob überhaupt Bundle-Inputs vorhanden sind
      var hasBundleInputs = this.form.querySelector('input[name^="items["]') !== null;
      if (!hasBundleInputs) return;

      var nextIndex = this._getMaxItemIndex() + 1;
      var bundleId = this._getBundleId();

      if (selected === 'printed' && this.isAvailable && this.variantId) {
        // Gedruckte Anleitung hinzufügen
        this._appendInput('items[' + nextIndex + '][id]', this.variantId);
        this._appendInput('items[' + nextIndex + '][quantity]', '1');
        this._appendInput('items[' + nextIndex + '][properties][Bundle]', this.bundleTitle);
        this._appendInput('items[' + nextIndex + '][properties][_BundleID]', bundleId);
        if (this.isFree) {
          this._appendInput('items[' + nextIndex + '][properties][_gratis_anleitung]', 'true');
          // Sichtbare Property: dokumentiert die Aktionsbedingung in Warenkorb,
          // Checkout, Bestellbestätigung und Admin-Bestellung (Widerrufsfall).
          this._appendInput('items[' + nextIndex + '][properties][Aktion]',
            'Gratis-Anleitung — bei Widerruf unversehrt zurücksenden, sonst separate Berechnung');
        }
      } else if (selected === 'digital' && this.digitalVariantId) {
        // Digitale Anleitung hinzufügen (wenn explizit wählbar, nicht auto-add)
        this._appendInput('items[' + nextIndex + '][id]', this.digitalVariantId);
        this._appendInput('items[' + nextIndex + '][quantity]', '1');
        this._appendInput('items[' + nextIndex + '][properties][Bundle]', this.bundleTitle);
        this._appendInput('items[' + nextIndex + '][properties][_BundleID]', bundleId);
      }
    }
  }

  /**
   * "Wichtiger Hinweis"-Box ausblenden wenn Anleitungs-Auswahl aktiv ist.
   * Die Box ist redundant wenn der Kunde direkt zwischen digital/gedruckt wählen kann.
   */
  function hideExternalLinkNotice() {
    var notice = document.querySelector('.instruction-notice');
    if (notice) notice.style.display = 'none';
  }

  // Initialisierung: Warten bis DOM bereit
  function init() {
    var containers = document.querySelectorAll('.instruction-choice');
    containers.forEach(function (container) {
      if (!container._instructionChoice) {
        container._instructionChoice = new InstructionChoice(container);
      }
    });

    // Notice ausblenden wenn mindestens eine Instruction-Choice vorhanden
    if (containers.length > 0) {
      hideExternalLinkNotice();
      // MutationObserver: Notice wird per JS nachträglich platziert, daher auch später prüfen
      if ('MutationObserver' in window) {
        var bodyObserver = new MutationObserver(function () {
          var notice = document.querySelector('.instruction-notice');
          if (notice && notice.style.display !== 'none') {
            notice.style.display = 'none';
            bodyObserver.disconnect();
          }
        });
        bodyObserver.observe(document.body, { childList: true, subtree: true });
        // Observer nach 10s aufräumen falls Notice nie erscheint
        setTimeout(function () { bodyObserver.disconnect(); }, 10000);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
